import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient, { formatApiError } from "../api";
import { Eye, EyeOff, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";

const PagesListPage = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/pages");
      setPages(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (page) => {
    const newStatus = page.status === "active" ? "hidden" : "active";
    try {
      await apiClient.patch(`/admin/pages/${page.id}`, { status: newStatus });
      toast.success(
        newStatus === "active" ? "Pagina geactiveerd" : "Pagina verborgen",
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-pages-list">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
            CMS · Pagina's
          </p>
          <h1 className="font-display text-5xl text-white tracking-tight">
            Pagina's
          </h1>
          <p className="mt-3 text-sm text-zinc-400 font-body max-w-xl">
            Beheer hero-titel, hero-afbeelding, intro, body en SEO per pagina.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Laden...</div>
      ) : (
        <div className="bg-ink-900 border border-white/[0.06]">
          <div className="grid grid-cols-12 px-6 py-4 hairline-b text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
            <div className="col-span-4">Titel</div>
            <div className="col-span-2">Key</div>
            <div className="col-span-3">Slug</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Acties</div>
          </div>
          {pages.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-page-row-${p.key}`}
              className="grid grid-cols-12 px-6 py-5 hairline-b last:border-b-0 items-center hover:bg-ink-850 transition-colors"
            >
              <div className="col-span-4 flex items-center gap-3">
                {p.is_core_page && (
                  <Lock
                    size={12}
                    className="text-champagne"
                    aria-label="Kernpagina"
                  />
                )}
                <span className="font-display text-xl text-white">
                  {p.title}
                </span>
              </div>
              <div className="col-span-2 text-xs text-zinc-400 font-body uppercase tracking-[0.2em]">
                {p.key}
              </div>
              <div className="col-span-3 text-xs text-zinc-500 font-body">
                /{p.slug}
              </div>
              <div className="col-span-2">
                <button
                  type="button"
                  onClick={() => toggleStatus(p)}
                  data-testid={`admin-page-toggle-${p.key}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase border transition-colors ${
                    p.status === "active"
                      ? "border-champagne/40 text-champagne"
                      : "border-white/10 text-zinc-500"
                  }`}
                >
                  {p.status === "active" ? (
                    <Eye size={11} />
                  ) : (
                    <EyeOff size={11} />
                  )}
                  {p.status === "active" ? "Actief" : "Verborgen"}
                </button>
              </div>
              <div className="col-span-1 flex justify-end">
                <Link
                  to={`/admin/pages/${p.id}`}
                  data-testid={`admin-page-edit-${p.key}`}
                  className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-champagne hover:text-champagne text-zinc-300 transition-colors"
                  aria-label="Bewerken"
                >
                  <Pencil size={13} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PagesListPage;
